import { useEffect } from 'react';
import { useFeatureFlags } from '@/context/FeatureFlagsContext';
import { shouldLoadEqualWeb, hasEqualWebDisabledCookie } from 'utils/shouldLoadEqualWeb';
import { isEmbeddedWindow, hasEmbedQueryParam } from 'utils/useIsEmbed';
import { reserveEqualWebPrototypeKeys } from 'utils/reserveEqualWebPrototypeKeys';

export const EQUAL_WEB_SCRIPT_ID = 'equalweb-accessibility';

const EQUAL_WEB_CDN = 'https://cdn.equalweb.com/';
const EQUAL_WEB_ACC_DOMAIN = 'https://access.equalweb.com/';
// VERIFY against the account's real embed code (held by Amir/Benjy, ENG-4334): core
// version path, sha512 integrity hash, acc domain (older accounts use aacdn.nagich.com),
// and any btnStyle customization from the EqualWeb dashboard.
const EQUAL_WEB_CORE_SRC = `${EQUAL_WEB_CDN}core/5.0.13/accessibility.js`;

interface InterdealConfig {
  sitekey: string;
  Position: 'left' | 'right';
  domains: { js: string; acc: string };
  Menulang: string;
}

declare global {
  interface Window {
    interdeal?: InterdealConfig;
  }
}

interface EqualWebAccessibilityProps {
  country: string | undefined;
  sitekey: string | null;
}

/**
 * EqualWeb accessibility overlay for UK customers (ENG-4334).
 *
 * Injected imperatively after hydration rather than rendered as JSX: the widget rewrites
 * the DOM broadly, which inside React's hydration root corrupts the tree (the same hazard
 * class documented in docs/consent-manager.md), and an external-only script keeps the CSP
 * `script-src` free of inline exceptions — the interdeal config is set from bundled code.
 *
 * Deliberately NOT consent-gated (`cmplazyload`): an accessibility tool must be available
 * before consent is resolved. Its cookies are classified in the ConsentManager dashboard.
 *
 * The `Object.prototype` keys are reserved before the loader is appended, or the widget
 * takes the resident login down with it — see `reserveEqualWebPrototypeKeys`.
 */
export function EqualWebAccessibility({ country, sitekey }: EqualWebAccessibilityProps) {
  const featureFlags = useFeatureFlags();

  useEffect(() => {
    const isEmbed = isEmbeddedWindow() || hasEmbedQueryParam(window.location.search);
    const isDisabledByCookie = hasEqualWebDisabledCookie(document.cookie);
    if (!shouldLoadEqualWeb({ country, sitekey, featureFlags, isEmbed, isDisabledByCookie }) || !sitekey) return;
    if (document.getElementById(EQUAL_WEB_SCRIPT_ID)) return;

    reserveEqualWebPrototypeKeys();

    window.interdeal = {
      sitekey,
      Position: 'left',
      domains: { js: EQUAL_WEB_CDN, acc: EQUAL_WEB_ACC_DOMAIN },
      Menulang: 'EN',
    };

    const script = document.createElement('script');
    script.id = EQUAL_WEB_SCRIPT_ID;
    script.src = EQUAL_WEB_CORE_SRC;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);
  }, [country, sitekey, featureFlags]);

  return null;
}
